import { Modal, Tooltip } from 'antd';
import { CameraDevice, Html5Qrcode } from 'html5-qrcode';
import { ChevronDown, Laptop, Wallet } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { setAuthTokens } from 'src/helpers/storage';
import useWindowSize from 'src/hooks/useWindowSize';
import { useAppTranslation } from 'src/i18n/hooks';
import { useAuth } from 'src/libs/rainbow';
import { emojiAvatarForAddress } from 'src/utils/emojiAvatarForAddress';
import WalletInformationModal from '../01.layout/header/WalletInformationModal';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import './styles/connect-wallet-button.scss';
import { jwtDecode } from 'jwt-decode';
import { useGetAllTimeLeaderboard } from 'src/services/leaderboard/get-all-time-leaderboard';
import { getTierImage } from 'src/pages/trading-leaderboard';
import { useAppSelector } from 'src/store';
import { IUserInfo } from 'src/store/reducers/user';
import { ellipseAddress } from 'src/helpers';
import { useWalletSelector } from '@near-wallet-selector/react-hook';
import { useGetUserInfo } from 'src/services/user/get-user-info';

let html5QrCode: Html5Qrcode;

const ScannerModal = () => {
  const [scanResult, setScanResult] = useState<string>();
  const { setAuthStatus } = useAuth();
  const [cameraList, setCameraList] = useState<CameraDevice[]>([]);
  const [activeCamera, setActiveCamera] = useState<CameraDevice>();
  const [isScanning, setIsScanning] = useState(false);
  const { t } = useAppTranslation(['common']);

  const onSuccess = useCallback((token: string) => {
    const decoded = jwtDecode(token) as any;
    const userAddress = decoded?.address;

    if (token && userAddress) {
      setScanResult(token);
      setAuthTokens({
        accessToken: token,
        refreshToken: token,
        desktopAddress: userAddress,
      });

      setAuthStatus('authenticated');
    }
  }, []);

  const getCameras = () => {
    Html5Qrcode.getCameras()
      .then((devices) => {
        /**
         * devices would be an array of objects of type:
         * { id: "id", label: "label" }
         */
        if (devices && devices.length) {
          setCameraList(devices);
          const foundBackCamera = devices.find((item) => item.label.includes('Back'));
          if (foundBackCamera) {
            setActiveCamera(foundBackCamera);
          } else {
            setActiveCamera(devices[0]);
          }

          html5QrCode
            .start(
              { facingMode: 'environment' },
              { fps: 20, qrbox: { width: 250, height: 250 } },
              onSuccess,
              (error) => {
                console.log(error);
              },
            )
            .then(() => {
              setIsScanning(true);
            });
        }
      })
      .catch(() => {
        setCameraList([]);
      });
  };

  const onCameraChange = async (value: string) => {
    const foundCamera = cameraList.find((item) => item.id === value);
    if (foundCamera) {
      setActiveCamera(foundCamera);
      if (html5QrCode.isScanning) {
        await html5QrCode.stop();
      }
      if (isScanning) {
        html5QrCode
          .start(
            foundCamera.id,
            { fps: 20, qrbox: { width: 250, height: 250 } },
            onSuccess,
            (error) => {
              console.log(error);
            },
          )
          .then(() => {
            setIsScanning(true);
          });
      }
    }
  };

  const handleScan = async () => {
    try {
      if (cameraList.length <= 0) {
        getCameras();
      } else {
        await html5QrCode.start(
          activeCamera?.id ?? { facingMode: 'environment' },
          { fps: 20, qrbox: { width: 250, height: 250 } },
          onSuccess,
          (error) => {
            console.log(error);
          },
        );
        setIsScanning(true);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleStop = () => {
    html5QrCode
      .stop()
      .then((res) => {
        html5QrCode.clear();
        setIsScanning(false);
      })
      .catch((err) => {
        console.log(err.message);
      });
  };

  useEffect(() => {
    html5QrCode = new Html5Qrcode('reader');
    getCameras();

    return () => {
      if (html5QrCode.isScanning) {
        html5QrCode.stop().then(() => {
          html5QrCode.clear();
        });
      } else {
        html5QrCode.clear();
      }
    };
  }, []);

  return (
    <div className="flex flex-col justify-center items-center gap-[10px]">
      <div className="mb-1">
        <p className="text-lg font-semibold mb-[5px]">{t('wallet.scanQRCode')}</p>
        <div
          dangerouslySetInnerHTML={{
            __html: t('wallet.scanQRDescription', {
              icon: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-tablet-smartphone inline"><rect width="10" height="14" x="3" y="8" rx="2"></rect><path d="M5 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2h-2.4"></path><path d="M8 18h.01"></path></svg>`,
            }),
          }}
        ></div>
      </div>
      {!scanResult ? (
        <div
          id="reader"
          style={{
            width: '300px',
          }}
        ></div>
      ) : null}

      <div className="flex w-full gap-[10px]">
        <div className="grow overflow-hidden">
          <Select onValueChange={onCameraChange} value={activeCamera?.id ?? ''}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select camera" />
            </SelectTrigger>
            <SelectContent
              className="z-[1000]"
              position="popper"
              sideOffset={5}
              style={{ position: 'relative' }}
            >
              {cameraList.map((item) => {
                return (
                  <SelectItem key={item.id} value={item.id}>
                    {item.label}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>

        <div className="max-w-[150px]">
          {isScanning ? (
            <Button
              className="bg-gradient-to-tr from-[#0f1fd0] via-[#7110e4] via-[#6f10e4] to-[#c403f6] text-white"
              onClick={handleStop}
            >
              {t('wallet.stopScan')}
            </Button>
          ) : (
            <Button
              className="bg-gradient-to-tr from-[#0f1fd0] via-[#7110e4] via-[#6f10e4] to-[#c403f6] text-white"
              onClick={handleScan}
            >
              {t('wallet.scan')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

const ConnectWalletButton = ({ onDeposit, onWithdraw }: { onDeposit: () => void, onWithdraw: () => void }) => {
  const { t } = useAppTranslation(['common']);
  const [open, setOpen] = useState(false);
  const [openScanner, setOpenScanner] = useState(false);
  const [openWalletInforModal, setOpenWalletInforModal] = useState(false);
  const { isTablet, isMobile } = useWindowSize();
  const userInfo: IUserInfo = useAppSelector((state) => state.user);
  const { isAuthenticated, setOpenSignMessageModal, isEthereumWallet, signIn } = useAuth();
  const { signedAccountId, signOut } = useWalletSelector();
  const { data: userData } = useGetUserInfo(localStorage.getItem('userId') ?? '');

  const isTestAccount = userData?.role === 'PAPER_ACCOUNT';

  const { data: AllTimeLeaderboardData } = useGetAllTimeLeaderboard(
    {
      page: 1,
      page_size: 10,
      useraddress: signedAccountId ?? '',
    },
    {
      enabled: !!signedAccountId,
    },
  );
  const { emoji, color } = emojiAvatarForAddress(signedAccountId ?? '');

  const handleConnectLinkWallet = () => {
    setOpenScanner(true);
    setOpen(false);
  };

  const handleConnect = async () => {
    if (isMobile || isTablet) {
      setOpen(true);
    } else {
      if (signedAccountId) {
        setOpenSignMessageModal(true);
      } else {
        await signIn?.();
      }
    }
  };

  const onConnectWalletsOption = async () => {
    setOpen(false);
    await signIn();
  };

  const renderTierImage = () => {
    if (isTestAccount) {
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          className="lucide lucide-user-lock-icon lucide-user-lock"
        >
          <path d="M19 16v-2a2 2 0 0 0-4 0v2" />
          <path d="M9.5 15H7a4 4 0 0 0-4 4v2" />
          <circle cx="10" cy="7" r="4" />
          <rect x="13" y="16" width="8" height="5" rx=".899" />
        </svg>
      );
    }

    return (
      <div
        style={{
          backgroundColor: !getTierImage(AllTimeLeaderboardData?.user?.tier) ? color : undefined,
        }}
        className="bg-[transparent] flex justify-center items-center w-[24px] h-[24px] rounded-[100px] dark:text-white"
      >
        {getTierImage(AllTimeLeaderboardData?.user?.tier) ? (
          <img
            className="w-[30px] h-[30px]"
            src={getTierImage(AllTimeLeaderboardData?.user?.tier)}
          />
        ) : (
          emoji
        )}
      </div>
    );
  };

  return (
    <div>
      {!isAuthenticated ? (
        <button
          className="w-[139px] h-[40px] bg-[#3898ff] rounded-[12px] font-[700] text-white hover:scale-[1.025]"
          onClick={handleConnect}
          type="button"
          id="connect-button"
        >
          {t('wallet.connect')}
        </button>
      ) : (
        <div style={{ display: 'flex', gap: 12 }}>
          {isTestAccount ? (
            <Tooltip title={t('account.testAccount')}>
              <div
                onClick={() => setOpenWalletInforModal(true)}
                className="cursor-pointer flex gap-[5px] items-center bg-white dark:!bg-[#1A1B1F] rounded-[12px] shadow-[0_4px_12px_rgba(0,0,0,0.1)] px-[8px] py-[6px] dark:bg-[#1A1B1F] dark:border-[1px] dark:border-[#1A1B1F] h-[40px] hover:scale-[1.025] transition"
              >
                {renderTierImage()}
                <span className="font-[700] leading-[26px] whitespace-nowrap dark:text-white">
                  {ellipseAddress(signedAccountId ?? userInfo.walletAddress)}
                </span>
                <ChevronDown size={20} className="dark:text-white" />
              </div>
            </Tooltip>
          ) : (
            <div
              onClick={() => setOpenWalletInforModal(true)}
              className="cursor-pointer flex gap-[5px] items-center bg-white dark:!bg-[#1A1B1F] rounded-[12px] shadow-[0_4px_12px_rgba(0,0,0,0.1)] px-[8px] py-[6px] dark:bg-[#1A1B1F] dark:border-[1px] dark:border-[#1A1B1F] h-[40px] hover:scale-[1.025] transition"
            >
              {renderTierImage()}
              <span className="font-[700] leading-[26px] whitespace-nowrap dark:text-white">
                {ellipseAddress(signedAccountId ?? userInfo.walletAddress)}
              </span>
              <ChevronDown size={20} className="dark:text-white" />
            </div>
          )}
        </div>
      )}
      <div>
        <Modal
          open={open}
          onCancel={() => setOpen(false)}
          footer={null}
          centered
          className="z-10 font-poppins"
        >
          <div className="flex flex-col gap-2 ">
            <div className="flex justify-center items-center">
              <h3 className="text-[18px]">{t('wallet.connectModalTitle')}</h3>
            </div>
            <div className="flex flex-col gap-[10px] mt-[20px]">
              <div className="flex items-center gap-[10px]" onClick={handleConnect}>
                <Wallet />
                <span>{t('wallet.connectWallets')}</span>
              </div>
              <div className="flex items-center gap-[10px]" onClick={handleConnectLinkWallet}>
                <Laptop />
                <span>{t('wallet.linkDesktopWallet')}</span>
              </div>
            </div>
          </div>
        </Modal>
        <div>
          <Modal
            onCancel={() => {
              setOpenScanner(false);
            }}
            className="scan-modal"
            footer={null}
            open={openScanner}
            destroyOnClose
            centered
          >
            <div className="py-[20px] max-h-[100vh] overflow-auto">
              <ScannerModal />
            </div>
          </Modal>
        </div>
        <WalletInformationModal
          onDeposit={onDeposit}
          onWithdraw={onWithdraw}
          walletAddress={signedAccountId ?? userInfo.walletAddress}
          open={openWalletInforModal}
          onClose={() => setOpenWalletInforModal(false)}
        />
        <Modal
          open={open}
          onCancel={() => setOpen(false)}
          footer={null}
          centered
          className="z-10 font-poppins"
        >
          <div className="flex flex-col gap-2 ">
            <div className="flex justify-center items-center">
              <h3 className="text-[18px]">{t('wallet.connectModalTitle')}</h3>
            </div>
            <div className="flex flex-col gap-[10px] mt-[20px]">
              <div
                className="flex items-center gap-[10px]"
                onClick={() => onConnectWalletsOption()}
              >
                <Wallet />
                <span>{t('wallet.connectWallets')}</span>
              </div>
              <div className="flex items-center gap-[10px]" onClick={handleConnectLinkWallet}>
                <Laptop />
                <span>{t('wallet.linkDesktopWallet')}</span>
              </div>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default ConnectWalletButton;
